<template>
  <v-container class="my-1">
    <v-card>
      <v-card-title class="page-head">
        <div class="ph-left">
          <div class="ph-icon"><v-icon color="#5e35b1">mdi-account-switch</v-icon></div>
          <div>
            <div class="ph-title">Transfer Child</div>
            <div class="ph-sub">Move children between care givers</div>
          </div>
        </div>
        <v-spacer />
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search..."
          outlined
          dense
          hide-details
          class="ph-search"
        />
        <v-btn class="ph-btn" @click="openForm()">
          <v-icon left small>mdi-plus</v-icon>
          New Transfer
        </v-btn>
      </v-card-title>

      <v-data-table
        class="elevation-4 transfer-table"
        :search="search"
        :headers="headers"
        :items="transfers"
        item-key="id"
        fixed-header
        :height="500"
      >
        <template v-slot:item.index="{ index }">
          <span class="cell-muted">{{ index + 1 }}</span>
        </template>

        <template v-slot:item.childname="{ item }">
          <span class="cell-name">{{ item.childname }}</span>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            small
            :color="item.status === 'Permanant' ? '#e8f5e9' : '#fff8e1'"
            :text-color="item.status === 'Permanant' ? '#2e7d32' : '#f57f17'"
          >
            {{ item.status }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="#2e7d32" v-bind="attrs" v-on="on" @click="edit(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Edit transfer</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="#c62828" v-bind="attrs" v-on="on" @click="remove(item.id)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Delete transfer</span>
          </v-tooltip>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="44" color="#b0bec5">mdi-account-switch-outline</v-icon>
            <div class="empty-text">No transfers found</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="700" persistent>
      <v-card>
        <div class="dlg-head">
          <div class="dlg-head-icon">
            <v-icon color="#fff">{{ oper === 'Add' ? 'mdi-account-switch' : 'mdi-account-edit' }}</v-icon>
          </div>
          <div>
            <div class="dlg-title">{{ oper === 'Add' ? 'New Transfer' : 'Edit Transfer' }}</div>
            <div class="dlg-sub">Move a child between care givers</div>
          </div>
          <v-spacer />
          <v-btn icon small class="dlg-close" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="pt-5">
          <v-form>
            <input type="hidden" v-model="form.id" />

            <div class="form-section">Transfer</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <label class="cell-muted d-block mb-1">Select Child</label>
                <Select2
                  v-model="form.child_id"
                  :options="childOptions"
                  id="transfer-child"
                  :settings="{ width: '100%' }"
                  @change="(val) => (form.child_id = val)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="cell-muted d-block mb-1">From Care Giver</label>
                <Select2
                  v-model="form.motherfrom_id"
                  :options="motherOptions"
                  id="mother-from"
                  :settings="{ width: '100%' }"
                  @change="(val) => (form.motherfrom_id = val)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="cell-muted d-block mb-1">To Care Giver</label>
                <Select2
                  v-model="form.motherto_id"
                  :options="motherToOptions"
                  id="mother-to"
                  :settings="{ width: '100%' }"
                  @change="(val) => (form.motherto_id = val)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="form.status" :items="['Temporary','Permanant']" label="Status" outlined dense prepend-inner-icon="mdi-swap-horizontal" required />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.transfer_date" type="date" label="Transfer Date" outlined dense prepend-inner-icon="mdi-calendar" />
              </v-col>
            </v-row>

            <div class="form-section">Notes</div>
            <v-row dense>
              <v-col cols="12">
                <v-textarea v-model="form.description" label="Description" rows="3" outlined dense prepend-inner-icon="mdi-text-box-outline" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="dlg-actions">
          <v-spacer />
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="#0d2d63" dark @click="save()">
            <v-icon left small>mdi-content-save-outline</v-icon>
            {{ oper === 'Add' ? 'Save Transfer' : 'Save Changes' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" bottom right color="blue">{{ text }}</v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import Select2 from 'v-select2-component'

// NOTE: backend must implement case 14 (or adjust below) to route to transfer_child stored procedure
const API_URL = process.env.VUE_APP_API_URL

// Configure opcodes here so we can change easily if your backend differs
const OPCION_TRANSFER = 14 // list/create/update/delete for transfer_child
const OP_LIST = 0
const OP_CREATE = 1
const OP_UPDATE = 11
const OP_DELETE = 12

export default {
  components: { Select2 },
  data () {
    return {
      search: '',
      dialog: false,
      oper: 'Add',
      snackbar: false,
      text: '',
      transfers: [],
      form: {
        id: '',
        motherfrom_id: '',
        motherto_id: '',
        child_id: '',
        description: '',
        status: 'Temporary',
        transfer_date: ''
      },
      headers: [
        { text: 'No.', value: 'index' },
        { text: 'Child', value: 'childname' },
        { text: 'From Care Giver', value: 'motherfrom' },
        { text: 'To Care Giver', value: 'motherto' },
        { text: 'Status', value: 'status' },
        { text: 'Description', value: 'description' },
        { text: 'Date', value: 'transfer_date' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      motherOptions: [],
      motherAllOptions: [],
      childOptions: []
    }
  },
  mounted () {
    this.fetchList()
    this.fetchAllMothers()
    this.fetchChildren() // load all children initially
  },
  computed: {
    motherToOptions () {
      const fromId = this.form && this.form.motherfrom_id
      if (!fromId) return this.motherAllOptions
      return (this.motherAllOptions || []).filter(m => String(m.id) !== String(fromId))
    }
  },
  watch: {
    'form.child_id' (val) {
      if (val) {
        this.fetchMothers(val)
      } else {
        this.motherOptions = []
      }
    },
    'form.motherfrom_id' (val) {
      // only refilter children when the user changes the from-mother manually
      if (this.oper === 'Add') this.fetchChildrenByMother(val)
    }
  },
  methods: {
    cleanDesc (text) {
      // strip legacy <br> tags stored by the old formatter
      return text ? String(text).replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ').trim() : ''
    },

    fetchMothers (idga) {
      // Returns the care giver(s) currently assigned to this child (opcion 45)
      axios
        .post(API_URL, { opcion: 45, id:idga, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then(r => {
          this.motherOptions = r.data || []
          // auto-fill "from" care giver when the child has exactly one assignment
          if (this.motherOptions.length === 1 && !this.form.motherfrom_id) {
            this.form.motherfrom_id = this.motherOptions[0].id
          }
        })
        .catch(err => {
          console.error('Failed to load mothers for transfer', err)
          this.motherOptions = []
        })
    },
    fetchAllMothers () {
      // Load all mothers (same as AssignchildVue opcion:4) for To Care Giver list
      axios
        .post(API_URL, { opcion: 46, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then(r => {
          this.motherAllOptions = r.data || []
        })
        .catch(err => {
          console.error('Failed to load all mothers for transfer', err)
          this.motherAllOptions = []
        })
    },
    fetchChildren () {
      // Only children with a live assignment can be transferred (opcion 15)
      axios
        .post(API_URL, { opcion: 15, onlyActive: 0, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then(r => {
          this.childOptions = r.data || []
        })
        .catch(err => {
          console.error('Failed to load children for transfer', err)
          this.childOptions = []
        })
    },
    fetchChildrenByMother (motherId) {
      // Children assigned to the selected care giver (opcion 15 supports mother_id)
      axios
        .post(API_URL, { opcion: 15, onlyActive: 0, mother_id: motherId || null, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then(r => {
          this.childOptions = r.data || []
        })
        .catch(err => {
          console.error('Failed to load children by mother for transfer', err)
        })
    },

    fetchList () {
      axios.post(API_URL, { opcion: OPCION_TRANSFER, op: OP_LIST, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' }).then(r => {
        this.transfers = (r.data || []).map(it => ({
          ...it,
          description: this.cleanDesc(it.description)
        }))
      })
    },

    openForm () {
      this.oper = 'Add'
      this.dialog = true
      this.form = { id: '', motherfrom_id: '', motherto_id: '', child_id: '', description: '', status: 'Temporary', transfer_date: '' }
    },
    edit (item) {
      this.oper = 'Update'
      this.dialog = true
      this.form = { ...item, description: this.cleanDesc(item.description) }
    },
    save () {
      if (!this.form.child_id || !this.form.motherfrom_id || !this.form.motherto_id) {
        this.snackbar = true
        this.text = 'Please select the child and both care givers'
        return
      }
      if (String(this.form.motherfrom_id) === String(this.form.motherto_id)) {
        this.snackbar = true
        this.text = 'From and To care giver cannot be the same'
        return
      }
      if (this.oper === 'Add') return this.create()
      return this.update()
    },
    create () {
      const p = {
        id: this.form.id,
        motherfrom_id: this.form.motherfrom_id,
        motherto_id: this.form.motherto_id,
        child_id: this.form.child_id,
        description: this.form.description,
        status: this.form.status,
        transfer_date: this.form.transfer_date,
        op: OP_CREATE,
        opcion: OPCION_TRANSFER,
        usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || ''
      }
      axios.post(API_URL, p).then((res) => {
        const err = res.data && res.data[0] && res.data[0].ok === false ? res.data[0].msg : ''
        if (err) {
          this.snackbar = true; this.text = err
          return
        }
        this.fetchList(); this.dialog = false; this.snackbar = true; this.text = 'Transfer added'
      }).catch(() => {
        this.snackbar = true; this.text = 'Network error. Please try again.'
      })
    },
    update () {
      const p = {
        id: this.form.id,
        motherfrom_id: this.form.motherfrom_id,
        motherto_id: this.form.motherto_id,
        child_id: this.form.child_id,
        description: this.form.description,
        status: this.form.status,
        transfer_date: this.form.transfer_date,
        op: OP_UPDATE,
        opcion: OPCION_TRANSFER,
        usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || ''
      }
      axios.post(API_URL, p).then((res) => {
        const err = res.data && res.data[0] && res.data[0].ok === false ? res.data[0].msg : ''
        if (err) {
          this.snackbar = true; this.text = err
          return
        }
        this.fetchList(); this.dialog = false; this.snackbar = true; this.text = 'Transfer updated'
      }).catch(() => {
        this.snackbar = true; this.text = 'Network error. Please try again.'
      })
    },
    remove (id) {
      axios.post(API_URL, { opcion: OPCION_TRANSFER, op: OP_DELETE, id }).then(() => {
        this.fetchList(); this.snackbar = true; this.text = 'Transfer deleted'
      })
    }
  }
}
</script>

<style scoped>
.transfer-table .v-data-table__wrapper { overflow-x: hidden }
.transfer-table table { table-layout: fixed; width: 100% }
.transfer-table th, .transfer-table td { white-space: normal; word-break: break-word }
</style>
